/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { reactSelectStyle } from "Config/Config";
import * as url from "helper/UrlHelper";
import {
  getData,
  postData,
  putData,
  uploadMultipleFile,
  uploadSingleFile,
} from "utils/Gateway";
import { fetchOptions } from "helper/Common/CommonHelper";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";
import { useTranslation } from "react-i18next";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";

// Import medical data
import { interventionTypeOptions } from "data/medical/interventionTypes";
import { roleOptions } from "data/medical/roles";
import { medicineOptions } from "data/medical/medicines";
import { labTestOptions } from "data/medical/labTests";
import { imagingOptions } from "data/medical/imaging";
import { therapyOptions } from "data/medical/therapies";
import { allergyOptions } from "data/medical/allergies";
import { pastMedicalIssuesOptions } from "data/medical/pastMedicalIssues";
import { treatmentStatusOptions } from "data/medical/treatmentstatus";
import { typeOptions } from "data/medical/treatmenttype";

// Helper function to format date to YYYY-MM-DD
const formatDateToInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display (DD-MM-YYYY)
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

// Helper function to map saved values to option objects
const mapValuesToOptions = (savedValues, optionsList) => {
  if (!savedValues || !Array.isArray(savedValues)) return [];
  
  return savedValues.map(savedValue => {
    const foundOption = optionsList.find(option => option.value === savedValue);
    return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
  }).filter(Boolean);
};

// Helper function to map single value to option object
const mapValuesToSingleOption = (savedValue, optionsList) => {
  if (!savedValue) return null;
  
  const foundOption = optionsList.find(option => option.value === savedValue);
  return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
};



// Step Modal component (editable)
const StepModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingStep, 
  phaseIndex,
  phaseStartDate,
  phaseDays
}) => {
  // ...existing code from SaveTreatment StepModal...
  const [id, setId] = useState(null);
  const [title, setTitle] = useState("");
  const [interventiontype, setInterventiontype] = useState(null);
  const [durationdays, setDurationdays] = useState("");
  const [instructiontopatient, setInstructiontopatient] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [imaging, setImaging] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [startdate, setStartdate] = useState("");
  const [enddate, setEnddate] = useState("");

  useEffect(() => {
    if (editingStep) {
      setId(editingStep._id || null);
      setTitle(editingStep.title || "");
      setInterventiontype(editingStep.interventiontype || null);
      setDurationdays(editingStep.durationdays || "");
      setInstructiontopatient(editingStep.instructiontopatient || "");
      setMedicines(editingStep.medicines || []);
      setLabTests(editingStep.labTests || []);
      setImaging(editingStep.imaging || []);
      setTherapies(editingStep.therapies || []);
      setStartdate(editingStep.startdate || "");
      setEnddate(editingStep.enddate || "");
    } else {
      resetForm();
    }
  }, [editingStep, isOpen]);

  const resetForm = () => {
    setId(null);
    setTitle("");
    setInterventiontype(null);
    setDurationdays("");
    setInstructiontopatient("");
    setMedicines([]);
    setLabTests([]);
    setImaging([]);
    setTherapies([]);
    setStartdate("");
    setEnddate("");
  };

  // AUTO-POPULATE: If step has no startdate but phase provides one, use it.
  useEffect(() => {
    if (!editingStep && !startdate && phaseStartDate && isOpen) {
      setStartdate(phaseStartDate);
    }
  }, [phaseStartDate, isOpen, editingStep]);

  // Additional effect to handle case when step is being edited but has no start date
  useEffect(() => {
    if (editingStep && !startdate && phaseStartDate) {
      setStartdate(phaseStartDate);
    }
  }, [editingStep, phaseStartDate]);

  // AUTO-CALCULATE enddate whenever startdate or durationdays changes.
  useEffect(() => {
    const daysToUse = durationdays || phaseDays;
    if (startdate && daysToUse) {
      try {
        const startDateObj = new Date(startdate);
        const endDateCalc = new Date(startDateObj);
        endDateCalc.setDate(startDateObj.getDate() + parseInt(daysToUse));
        const calculatedEndDate = formatDateToInput(endDateCalc);
        if (enddate !== calculatedEndDate) {
          setEnddate(calculatedEndDate);
        }
      } catch (err) {
        // invalid date - ignore
      }
    }
  }, [startdate, durationdays, phaseDays, enddate]);

  const handleSave = () => {
    const stepData = {
      _id: id,
      title,
      interventiontype,
      durationdays: parseInt(durationdays) || 0,
      instructiontopatient,
      medicines,
      labTests,
      imaging,
      therapies,
      startdate,
      enddate
    };
    
    onSave(stepData, phaseIndex, editingStep?.stepIndex);
    resetForm();
    onClose();
  };

  // Custom styles for white background
  const whiteSelectStyle = {
    ...reactSelectStyle("3px", "6px", true, false),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: '#dee2e6',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : 'white',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
      },
    }),
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              {editingStep ? 'Edit Step' : 'Add Step'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {/* ...existing code from SaveTreatment StepModal body... */}
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">Step Name *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter step name"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Type of Intervention *</label>
                <Select
                  options={interventionTypeOptions}
                  value={interventiontype}
                  onChange={setInterventiontype}
                  isClearable
                  placeholder="Select intervention type"
                  styles={whiteSelectStyle}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Duration (Days) *</label>
                <input
                  type="number"
                  min="1"
                  value={durationdays}
                  onChange={(e) => setDurationdays(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter duration in days"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Start Date</label>
                <input
                  type="date"
                  value={startdate}
                  onChange={(e) => setStartdate(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                />
                <small className="text-muted">
                  If empty, will default to phase start date (if available).
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">End Date</label>
                <input
                  type="date"
                  value={enddate}
                  onChange={(e) => setEnddate(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  title="Auto-calculated from start date and duration. You can override if needed."
                />
                <small className="text-muted">Auto-calculated from Start Date + Duration (days)</small>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">Instructions to Patient</label>
                <textarea
                  rows="4"
                  value={instructiontopatient}
                  onChange={(e) => setInstructiontopatient(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter instructions for the patient..."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!title || !interventiontype || !durationdays}
            >
              Save Step
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Block component (read-only display with edit capability)
const StepBlock = ({ step, stepIndex, phaseIndex, editStep, deleteStep, isViewMode = false }) => {
  return (
    <div className="border rounded-6 p-3 mb-2 bg-light position-relative">
      {!isViewMode && (
        <div className="position-absolute top-0 end-0 p-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary me-1"
            onClick={() => editStep(phaseIndex, stepIndex)}
            title="Edit Step"
            style={{ width: "28px", height: "28px", padding: "0" }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
              edit
            </span>
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => deleteStep(phaseIndex, stepIndex)}
            title="Delete Step"
            style={{ width: "28px", height: "28px", padding: "0" }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
              delete
            </span>
          </button>
        </div>
      )}
      
      <div className="row">
        <div className="col-md-8">
          <h6 className="fw-semibold text-primary mb-1">{step.title}</h6>
          <p className="mb-1 fs-sm"><strong>Type:</strong> {step.interventiontype?.label}</p>
          {(step.startdate || step.enddate) && (
            <p className="mb-1 fs-sm">
              <strong>Timeline:</strong> 
              {step.startdate && <span className="text-success"> Start: {formatDateForDisplay(step.startdate)}</span>}
              {step.startdate && step.enddate && <span> | </span>}
              {step.enddate && <span className="text-warning"> End: {formatDateForDisplay(step.enddate)}</span>}
            </p>
          )}
          <p className="mb-1 fs-sm"><strong>Duration:</strong> {step.durationdays} days</p>
        </div>
        {/* ...existing code for medicines, labTests, imaging, therapies display... */}
      </div>
      
      {step.instructiontopatient && (
        <div className="mt-2">
          <p className="mb-0 fs-sm"><strong>Instructions:</strong> {step.instructiontopatient}</p>
        </div>
      )}
    </div>
  );
};

// Phase Block component (read-only with editable steps)
const PhaseViewBlock = ({
  phase,
  index,
  editStep,
  deleteStep,
  addStepToPhase,
}) => {
  const accordionId = `phase-view-accordion-${index}`;
  const collapseId = `phase-view-collapse-${index}`;
  const headingId = `view-heading-${index}`;
  
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepModal(true);
  };

  const handleEditStep = (phaseIndex, stepIndex) => {
    const step = phase.steps[stepIndex];
    setEditingStep({ ...step, stepIndex });
    setShowStepModal(true);
  };

  const handleSaveStep = (stepData, phaseIndex, stepIndex) => {
    if (stepIndex !== undefined) {
      // Edit existing step
      editStep(index, stepIndex, stepData);
    } else {
      // Add new step
      addStepToPhase(index, stepData);
    }
  };

  const handleDeleteStep = (phaseIndex, stepIndex) => {
    deleteStep(index, stepIndex);
  };

  return (
    <>
      <div className="accordion mb-3" id={accordionId}>
        <div className="accordion-item border border-gray-300 rounded-8 shadow-sm">
          <h6
            className="accordion-header d-flex align-items-center"
            id={headingId}
          >
            <button
              className="accordion-button collapsed bg-light fw-semibold py-3 px-3 border-0 rounded-top-8 flex-grow-1"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#${collapseId}`}
              aria-expanded="false"
              aria-controls={collapseId}
            >
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center">
                  <span className="text-primary me-2">Phase {index + 1}</span>
                  {phase.title && (
                    <span className="text-muted"> : {phase.title}</span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 me-3">
                  {phase.steps && phase.steps.length > 0 && (
                    <span className="badge bg-info">{phase.steps.length} steps</span>
                  )}
                  {phase.days && (
                    <span className="badge bg-success">{phase.days} days</span>
                  )}
                </div>
              </div>
            </button>
          </h6>
          <div
            id={collapseId}
            className="accordion-collapse collapse"
            aria-labelledby={headingId}
            data-bs-parent={`#${accordionId}`}
          >
            <div className="accordion-body p-3 bg-white">
              {/* Read-only Phase Information */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">Phase Name:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.title || '-'}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">Responsible Person:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.responsibleperson?.label || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">Duration:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.days ? `${phase.days} days` : '-'}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">Start Date:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.startdate ? formatDateForDisplay(phase.startdate) : '-'}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">End Date:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.enddate ? formatDateForDisplay(phase.enddate) : '-'}</p>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <div className="mb-2">
                    <strong className="fs-sm text-dark">Description:</strong>
                    <p className="mb-0 fs-sm text-muted">{phase.description || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Steps Section - Editable */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0">Steps</h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleAddStep}
                  >
                    <span className="material-symbols-outlined">add</span>
                    Add Steps
                  </button>
                </div>

                {phase.steps && phase.steps.length > 0 ? (
                  <div>
                    {phase.steps.map((step, stepIndex) => (
                      <StepBlock
                        key={stepIndex}
                        step={step}
                        stepIndex={stepIndex}
                        phaseIndex={index}
                        editStep={handleEditStep}
                        deleteStep={handleDeleteStep}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-muted">
                    <span className="material-symbols-outlined fs-4">assignment</span>
                    <p className="mb-0 fs-sm">No steps added yet. Click "Add Steps" to start.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Modal */}
      <StepModal
        isOpen={showStepModal}
        onClose={() => setShowStepModal(false)}
        onSave={handleSaveStep}
        editingStep={editingStep}
        phaseIndex={index}
        phaseStartDate={phase.startdate}
        phaseDays={phase.days}
      />
    </>
  );
};

const AskTreatmentConsent = ({
  selectedTreatmentId,
  setSelectedTreatmentId,
  afterActionClose,
  setShowAlert,
  setAlertMessage,
  setMessageType,
}) => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [treatmentData, setTreatmentData] = useState(null);

  // New state: consent selection (radio + checkboxes)
  // mode: 'full' or 'phases'
  const [consentMode, setConsentMode] = useState("phases");
  const [selectedPhaseIds, setSelectedPhaseIds] = useState([]);

  //function for get treatment details
  const getTreatmentDetails = async () => {
    try {
      setIsLoading(true);
      
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TREATMENT_BY_ID +
        `/${selectedTreatmentId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const templateData = response.data;
        setTreatmentData(templateData);

        // Map phases for display
        if (templateData.projectsections && templateData.projectsections.length > 0) {
          templateData.projectsections.forEach((phase, index) => {
            // Map actual responsible person if exists
            if (phase.responsibleperson) {
              phase.responsibleperson = {
                value: phase.responsibleperson._id,
                label: `${phase.responsibleperson.name || ""} ${phase.responsibleperson.surname || ""}`.trim() || phase.responsibleperson.name || phase.responsibleperson.email
              };
            } else {
              phase.responsibleperson = null;
            }

            // Set phase dates if they exist
            phase.startdate = phase.startdate || "";
            phase.enddate = phase.enddate || "";

            // Map steps data to correct field names
            if (phase.projecttasks && phase.projecttasks.length > 0) {
              phase.steps = phase.projecttasks.map(step => ({
                _id: step._id,
                title: step.title || step.stepName,
                interventiontype: step.medicalextension ? 
                  mapValuesToSingleOption(step.medicalextension.interventiontype, interventionTypeOptions) : null,
                durationdays: step.medicalextension ? step.medicalextension.durationdays || 0 : 0,
                instructiontopatient: step.medicalextension?.instructiontopatient ?? '',
                medicines: mapValuesToOptions(step.medicalextension?.medicines || [], medicineOptions),
                labTests: mapValuesToOptions(step.medicalextension?.labTests || [], labTestOptions),
                imaging: mapValuesToOptions(step.medicalextension?.imaging || [], imagingOptions),
                therapies: mapValuesToOptions(step.medicalextension?.therapies || [], therapyOptions),
                startdate: step.startdate || "",
                enddate: step.enddate || "",
              }));
            }
          });
        }
      }
    } catch (error) {
      console.log(error.message);
      setAlertMessage("Error loading treatment details");
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTreatmentId) {
      getTreatmentDetails();
    }
  }, [selectedTreatmentId]);

  useEffect(() => {
    // Reset consent selections when treatment changes
    setConsentMode("phases");
    setSelectedPhaseIds([]);
  }, [selectedTreatmentId]);

  const togglePhaseSelection = (phaseId) => {
    setSelectedPhaseIds((prev) => {
      if (prev.includes(phaseId)) return prev.filter((id) => id !== phaseId);
      return [...prev, phaseId];
    });
  };

  const handleConsentModeChange = (mode) => {
    setConsentMode(mode);
    if (mode === "full") {
      const allIds = (treatmentData?.projectsections || []).map((p) => p._id || p.id).filter(Boolean);
      setSelectedPhaseIds(allIds);
    } else {
      setSelectedPhaseIds([]);
    }
  };

  const submitConsentRequest = async () => {
    if (consentMode !== "full" && (!selectedPhaseIds || selectedPhaseIds.length === 0)) {
      setAlertMessage("Please select at least one phase or choose Full consent.");
      setMessageType("error");
      setShowAlert(true);
      return;
    }

    const payload = {
      treatmentId: selectedTreatmentId,
      fullConsent: consentMode === "full",
      phases: consentMode === "full" ? (treatmentData?.projectsections || []).map(p => p._id || p.id).filter(Boolean) : selectedPhaseIds,
      requestedBy: userInfo?._id || null,
      requestedAt: new Date().toISOString(),
    };

    try {
      setIsLoading(true);
      const requestUrl = url.API_BASE_URL + "/treatment-consents?token=" + token;
      const response = await postData(requestUrl, payload);

      if (response && response.status) {
        setAlertMessage("Consent request submitted successfully.");
        setMessageType("success");
        setShowAlert(true);
        if (afterActionClose) afterActionClose();
        // reset local selection
        setConsentMode("phases");
        setSelectedPhaseIds([]);
        setTreatmentData(null);
        setSelectedTreatmentId(null);
      } else {
        setAlertMessage(response?.message || "Failed to submit consent request.");
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Error submitting consent request.");
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading skeleton UI
  if (isLoading || !treatmentData) {
    return (
      <div
        className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
        tabIndex="-1"
        id="askConsentTreatmentOffCanvas"
        aria-labelledby="askConsentTreatmentOffCanvas"
      >
        {/* Header */}
        <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
            <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary fs-5 icon-md">
                visibility
                </span>
            </div>
            <div>
              <h3 className="offcanvas-title fw-bold text-white mb-0">
                View Treatment Plan
              </h3>
              <p className="text-white fs-sm mb-0">
                View treatment plan details
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            //onClick={resetHandler}
          ></button>
        </div>
        
        <div className="offcanvas-body p-4">
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
            {/* Loading skeleton placeholder */}
            <div className="placeholder-glow d-flex flex-column gap-4">
              <span className="placeholder placeholder-lg bg-secondary col-12"></span>
              <span className="placeholder placeholder-lg bg-secondary col-8"></span>
              <span className="placeholder placeholder-lg bg-secondary col-4"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="askConsentTreatmentOffCanvas"
      aria-labelledby="askConsentTreatmentOffCanvas"
    >
      {/* Header */}

      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              medical_information
            </span>
          </div>
          <div>
            <h3 className="offcanvas-title fw-bold text-white mb-0">
              {treatmentData?.title || 'Treatment Plan Details'}
            </h3>
            <p className="text-white fs-sm mb-0">
              Treatment Plan Overview
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          //onClick={resetHandler}
        ></button>
      </div>
      
      <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container-fluid p-0">
          
          {/* Care Team & Patient Card */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-primary bg-opacity-10 border-0 py-3">
              <div className="d-flex align-items-center gap-2">
                <span className="material-symbols-outlined text-primary fs-5">
                  people
                </span>
                <h5 className="mb-0 fw-semibold text-primary">{t("Care Team & Patient")}</h5>
              </div>
            </div>
            <div className="card-body p-4 bg-white">
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <div className="icon-wrapper">
                      <span className="material-symbols-outlined text-success fs-4">
                        person
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Patient</label>
                      {treatmentData?.medicalextension?.patient ? (
                        <div>
                          <p className="mb-1 text-dark fs-6 fw-medium">
                            {`${treatmentData.medicalextension.patient.name || ''} ${treatmentData.medicalextension.patient.surname || ''}`.trim()}
                          </p>
                          <p className="mb-0 text-muted fs-7">
                            <span className="material-symbols-outlined fs-6 me-1">email</span>
                            {treatmentData.medicalextension.patient.email}
                          </p>
                        </div>
                      ) : (
                        <span className="text-secondary fst-italic">Not assigned</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <div className="icon-wrapper">
                      <span className="material-symbols-outlined text-info fs-4">
                        medical_services
                      </span>
                    </div>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Attending Doctor</label>
                      {treatmentData?.medicalextension?.doctor ? (
                        <div>
                          <p className="mb-1 text-dark fs-6 fw-medium">
                            {`${treatmentData.medicalextension.doctor.name || ''} ${treatmentData.medicalextension.doctor.surname || ''}`.trim()}
                          </p>
                          <p className="mb-0 text-muted fs-7">
                            <span className="material-symbols-outlined fs-6 me-1">email</span>
                            {treatmentData.medicalextension.doctor.email}
                          </p>
                        </div>
                      ) : (
                        <span className="text-secondary fst-italic">Not assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Treatment Information Card */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-success bg-opacity-10 border-0 py-3">
              <div className="d-flex align-items-center gap-2">
                <span className="material-symbols-outlined text-success fs-5">
                  assignment
                </span>
                <h5 className="mb-0 fw-semibold text-success">{t("Treatment Information")}</h5>
              </div>
            </div>
            <div className="card-body p-4 bg-white">
              <div className="row g-4">
                <div className="col-md-8">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <span className="material-symbols-outlined text-primary fs-4">title</span>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Treatment Name</label>
                      <p className="mb-0 text-dark fs-6 fw-medium">
                        {treatmentData?.title || '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <span className="material-symbols-outlined text-warning fs-4">flag</span>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Treatment Status</label>
                      {(() => {
                        // Map treatment status using the same logic as SaveTreatment
                        if (treatmentData?.medicalextension && treatmentData.medicalextension.treatmentstatus) {
                          const mappedTreatmentStatus = mapValuesToSingleOption(
                            treatmentData.medicalextension.treatmentstatus,
                            treatmentStatusOptions
                          );
                          const statusColor = 
                            mappedTreatmentStatus?.value === 'completed' ? 'bg-success' :
                            mappedTreatmentStatus?.value === 'ongoing' ? 'bg-primary' :
                            mappedTreatmentStatus?.value === 'planned' ? 'bg-info' :
                            mappedTreatmentStatus?.value === 'paused' ? 'bg-warning' :
                            mappedTreatmentStatus?.value === 'cancelled' ? 'bg-danger' : 'bg-secondary';
                          
                          return (
                            <span className={`badge px-3 py-2 fs-7 ${statusColor}`}>
                              {mappedTreatmentStatus?.label || 'UNKNOWN'}
                            </span>
                          );
                        }
                        return (
                          <span className="badge px-3 py-2 fs-7 bg-secondary">
                            NOT SET
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-4 mt-2">
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <span className="material-symbols-outlined text-purple fs-4">category</span>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Medical Specialty</label>
                      <p className="mb-0 text-muted fs-6">
                        {treatmentData?.projecttypeid?.name || '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <span className="material-symbols-outlined text-success fs-4">event</span>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">Start Date</label>
                      <p className="mb-0 text-muted fs-6">
                        {treatmentData?.startdate ? formatDateForDisplay(treatmentData.startdate) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start gap-3 p-3 rounded-3 border border-light bg-light bg-opacity-50">
                    <span className="material-symbols-outlined text-danger fs-4">event_available</span>
                    <div className="flex-grow-1">
                      <label className="fw-semibold text-dark mb-1 d-block">End Date</label>
                      <p className="mb-0 text-muted fs-6">
                        {treatmentData?.enddate ? formatDateForDisplay(treatmentData.enddate) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Redesigned Consent Request Card */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-info bg-opacity-10 border-0 py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <span className="material-symbols-outlined text-info fs-5">
                  how_to_reg
                </span>
                <h5 className="mb-0 fw-semibold text-info">Request Consent</h5>
              </div>
            </div>

            <div className="card-body p-4 bg-white">
              {/* Professional radio group */}
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="consentMode"
                    id="consentFull"
                    checked={consentMode === "full"}
                    onChange={() => handleConsentModeChange("full")}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="consentFull">
                    Request full consent for the entire treatment
                  </label>
                  <div className="text-muted fs-sm ps-4 mt-1">
                    This will request consent for all phases and associated steps.
                  </div>
                </div>

                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="consentMode"
                    id="consentPhases"
                    checked={consentMode === "phases"}
                    onChange={() => handleConsentModeChange("phases")}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="consentPhases">
                    Request consent for selected phases
                  </label>
                  <div className="text-muted fs-sm ps-4 mt-1">
                    Select specific phases below and request consent for them and their steps.
                  </div>
                </div>
              </div>

              <hr />

              {/* Vertical list of phases with checkboxes and descriptive text */}
              <div className="d-flex flex-column gap-2">
                {(treatmentData.projectsections || []).length === 0 && (
                  <div className="text-center text-muted">No phases available for this treatment.</div>
                )}

                {(treatmentData.projectsections || []).map((phase, idx) => {
                  const phaseId = phase._id || phase.id || `${idx}`;
                  const stepCount = (phase.projecttasks || []).length;
                  const checked = selectedPhaseIds.includes(phaseId);

                  return (
                    <div key={phaseId} className="border rounded-3 p-3 d-flex justify-content-between align-items-start">
                      <div className="pe-3">
                        <div className="fw-semibold text-dark">Phase {idx + 1}: {phase.title || 'Untitled Phase'}</div>
                        <div className="fs-sm text-muted mt-1">
                          Steps invoiced in this phase:
                          <span className="badge px-2 py-1 fs-7 bg-info ms-2">{stepCount}</span>
                        </div>

                        <div className="fs-sm text-muted mt-2 ps-1">
                          {checked
                            ? `Request consent for this phase and associated ${stepCount} step${stepCount !== 1 ? 's' : ''}.`
                            : `You can request consent for this phase and its ${stepCount} step${stepCount !== 1 ? 's' : ''}.`}
                        </div>

                        {/* List each step with its intervention type (or title fallback) */}
                        {(phase.steps || []).length > 0 && (
                          <ul className="list-unstyled fs-sm mt-2 mb-0">
                            {phase.steps.map((s, si) => (
                              <li key={s._id || si} className="text-muted">
                                <strong className="text-dark">Step {si + 1}:</strong>{" "}
                                <span
                                  className="interventiontype badge bg-white text-primary fw-medium"
                                  style={{
                                    fontSize: "0.92rem",
                                    padding: "0.18rem 0.5rem",
                                    borderRadius: "6px",
                                    border: "1px solid #e6eef6",
                                    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {s.interventiontype?.label || s.title || "-"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      
                      {/* Highlighted box for checkbox + label */}
                      <div className="ms-3 d-flex align-items-center" style={{ minWidth: 160 }}>
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 px-3 py-2"
                          style={{
                            backgroundColor: checked ? '#e6f7ff' : (consentMode === 'full' ? '#f1f5f9' : '#eef9ff'),
                            border: checked ? '1px solid #60b3d6' : '1px solid #e6eef6',
                            boxShadow: checked ? '0 4px 10px rgba(96,179,214,0.08)' : 'none',
                          }}
                        >
                          <input
                            className="form-check-input me-2"
                            type="checkbox"
                            id={`phase-consent-${phaseId}`}
                            checked={checked}
                            onChange={() => togglePhaseSelection(phaseId)}
                            disabled={consentMode === "full"}
                            style={{ width: 18, height: 18 }}
                          />
                          <label
                            className="form-check-label fw-semibold mb-0"
                            htmlFor={`phase-consent-${phaseId}`}
                            style={{ cursor: consentMode === "full" ? 'not-allowed' : 'pointer' }}
                          >
                            Request Consent
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    // reset & close panel
                    setConsentMode("phases");
                    setSelectedPhaseIds([]);
                    if (afterActionClose) afterActionClose();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitConsentRequest}
                  disabled={isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit Consent Request'}
                </button>
              </div>
            </div>
          </div>

          {/* ...other sections if any (kept as labels only) ... */}
        </div>
      </div>
    </div>
  );
};

export default AskTreatmentConsent;
